import { FC, useEffect, useState } from 'react';

import AccountCard from '@eneco/components-next/AccountCard/AccountCard';
import useDC from '@eneco/dc/useDC';
import { useCustomerProfileAccount } from '@eneco/hooks/profile';
import { Button, Stack } from '@eneco/sparky';

import type { AccountSelectStepContent } from './account-select-step-content.types';
import { useCachedAccount } from './useCachedAccount';
import { Header, Layout } from '../../components';
import { useStepContent } from '../../hooks';
import { useFlowHooks } from '../../utils/FlowProvider';

export const AccountSelectStep: FC = () => {
  const isReady = useCachedAccount();
  const { accountId } = useDC();
  const { useFlowActorRef } = useFlowHooks();
  const sendToFlowMachine = useFlowActorRef().send;

  const { hasMultipleAccounts, isLoading: isProfileLoading } = useCustomerProfileAccount({ filterInactive: true });
  const textualData = useStepContent<AccountSelectStepContent>('AccountSelectStep');
  const [isDataLoading, setIsDataLoading] = useState<boolean>(true);

  function submitAccount() {
    sendToFlowMachine({
      type: 'NEXT_PAGE',
      values: { accountId },
    });
  }

  useEffect(() => {
    if (!isProfileLoading && !hasMultipleAccounts) {
      submitAccount();
    }
  }, [hasMultipleAccounts, isProfileLoading]);

  useEffect(() => {
    if (isReady && !isProfileLoading && hasMultipleAccounts) {
      setIsDataLoading(false);
    } else {
      setIsDataLoading(true);
    }
  }, [isReady, isProfileLoading, hasMultipleAccounts]);

  // `selectAccountDialog` is renamed to `modalDialog` and gains an empty
  // subheading, which the card has but this step's content does not.
  const customLabels = textualData?.accountSelect
    ? {
        ...textualData.accountSelect,
        modalDialog: { ...textualData.accountSelect.selectAccountDialog, subheading: '' },
      }
    : undefined;

  return (
    <Layout.Content variant="A" isLoading={isDataLoading}>
      <Header>
        <Header.Title>{textualData?.content?.title}</Header.Title>
      </Header>
      <AccountCard
        compactLayout
        customLabels={customLabels}
        onAccountSwitch={accountId => sendToFlowMachine({ type: 'UPDATE_VALUES', values: { accountId } })}
      />
      <Stack direction={{ initial: 'column', md: 'row' }}>
        <Button onClick={submitAccount}>{textualData?.content?.nextStepText}</Button>
      </Stack>
    </Layout.Content>
  );
};
